/*
 * <cart-auto-update> — Cart page automatic update.
 * Removes the need for a "Update cart" button: changing a quantity (typing or
 * the +/- buttons) updates the cart via the Ajax API and re-renders the cart
 * section so line totals, the subtotal and prices refresh automatically.
 */
(function () {
  class CartAutoUpdate extends HTMLElement {
    constructor() {
      super();
      this.pending = {};
      this.loading = false;
      this.timer = null;
      this.lastKey = null;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      // Capture phase so we run BEFORE the theme's own +/- handler and can
      // both do the math ourselves and stop it from double-counting.
      this.addEventListener('click', this.onClick, true);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
    }

    onClick(e) {
      // Remove link → set quantity to 0
      const remove = e.target.closest('[data-cart-remove]');
      if (remove && this.contains(remove)) {
        e.preventDefault();
        e.stopPropagation();
        this.queue(remove.getAttribute('data-quantity-key'), 0);
        return;
      }

      // +/- quantity buttons
      const btn = e.target.closest('.js-qty__adjust');
      if (!btn || !this.contains(btn)) return;
      const wrap = btn.closest('.js-qty');
      const input = wrap && wrap.querySelector('[data-quantity-input]');
      if (!input) return;

      e.preventDefault();
      e.stopPropagation();

      const min = parseInt(input.getAttribute('min'), 10) || 0;
      let val = parseInt(input.value, 10) || 0;
      if (btn.classList.contains('js-qty__adjust--minus')) {
        val = Math.max(min, val - 1);
      } else {
        val = val + 1;
      }
      input.value = val;
      this.queue(input.getAttribute('data-quantity-key'), val);
    }

    onChange(e) {
      const input = e.target.closest('[data-quantity-input]');
      if (!input) return;
      const min = parseInt(input.getAttribute('min'), 10) || 0;
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 0) val = min;
      if (val !== 0 && val < min) val = min;
      input.value = val;
      this.queue(input.getAttribute('data-quantity-key'), val);
    }

    onKeydown(e) {
      if (e.key === 'Enter' && e.target.closest('[data-quantity-input]')) {
        // Avoid submitting the form (which would jump to checkout)
        e.preventDefault();
        e.target.blur();
      }
    }

    queue(key, qty) {
      if (!key) return;
      this.pending[key] = qty;
      this.lastKey = key;
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.flush(), 350);
    }

    flush() {
      if (this.loading) return; // will retry from finally()
      const updates = this.pending;
      if (!Object.keys(updates).length) return;
      this.pending = {};
      this.loading = true;
      this.classList.add('is-loading');

      const url = (window.routes && window.routes.cart_update_url) || '/cart/update';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.sections && data.sections[this.sectionId]) {
            this.render(data.sections[this.sectionId]);
          }
          // Keep the header cart count / drawer in sync
          if (window.wetheme && typeof window.wetheme.updateCartCount === 'function') {
            try { window.wetheme.updateCartCount(data); } catch (err) {}
          }
          document.documentElement.dispatchEvent(
            new CustomEvent('wetheme-cart-update', { detail: { cart: data } })
          );
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.classList.remove('is-loading');
          if (Object.keys(this.pending).length) this.flush();
        });
    }

    render(html) {
      const container = this.querySelector('[data-cart-contents]');
      if (!container) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-contents]');
      if (!fresh) return;

      // The cart markup uses scroll-reveal animations (opacity:0 until observed).
      // Re-injected nodes are never observed, so reveal them immediately.
      fresh.querySelectorAll('.show-on-scroll').forEach((el) => {
        el.classList.remove('show-on-scroll');
        el.classList.add('shown-on-scroll');
      });
      fresh.querySelectorAll('[data-animate]').forEach((el) => el.removeAttribute('data-animate'));

      container.innerHTML = fresh.innerHTML;

      // Restore focus on the field that was just edited so repeated clicks work.
      if (this.lastKey) {
        const input = container.querySelector(
          '[data-quantity-input][data-quantity-key="' + this.lastKey + '"]'
        );
        if (input) {
          input.focus();
          const v = input.value;
          input.value = '';
          input.value = v;
        }
      }
    }
  }

  if (!customElements.get('cart-auto-update')) {
    customElements.define('cart-auto-update', CartAutoUpdate);
  }
})();